import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import LocationSelector from './LocationSelector';
import SignatureCanvasModal from './SignatureCanvas';
import { checkQuestionVisibility } from '../../utils/questionUtils';

/**
 * QuestionRenderer - Render question input based on type
 * 
 * Supports:
 * - Text, TextArea, Number, Date, Time, DateTime
 * - RadioButton, DropDown, Checkbox
 * - LocationSelector
 * - Recursive nested questions (answer branches)
 * - Repeatable questions (root questions only)
 * 
 * @param {Object} question - Question object with type, displayText, options, etc.
 * @param {any} value - Current answer value
 * @param {Function} onChange - Callback when answer changes (key, value)
 * @param {Object} answers - All answers object (for nested question context)
 * @param {Function} onNestedChange - Callback for nested question changes (optional)
 * @param {Array} allQuestions - All questions array (for LocationSelector dependsOn lookup)
 * @param {number} level - Nesting level for indentation (default: 0)
 */
const QuestionRenderer = ({
  question,
  value,
  onChange,
  answers = {},
  onNestedChange,
  allQuestions = [],
  level = 0,
}) => {
  // CRITICAL: Hooks must be called before any early returns
  const [signatureModalOpen, setSignatureModalOpen] = React.useState(false);
  // Stable IDs for repeatable item React keys — prevents component reuse on deletion
  const itemIdsRef = React.useRef([]);

  // Validate question object
  if (!question || !question.type) {
    console.error('[QuestionRenderer] Invalid question object:', question);
    return (
      <Box>
        <Typography variant="body2" color="error">
          Geçersiz soru objesi: {question ? JSON.stringify(question) : 'null'}
        </Typography>
      </Box>
    );
  }

  const handleChange = (newValue) => {
    if (onChange) {
      onChange(question.key, newValue);
    }
  };

  // Handle repeatable field changes (array of values)
  // For repeatable questions with nested questions, each item should be an object
  // { mainValue: ..., nestedAnswers: { key1: value1, key2: value2 } }
  const handleRepeatableChange = (index, itemValue) => {
    const currentArray = Array.isArray(value) ? [...value] : [];
    
    // If this is a repeatable question with nested questions, we need to handle it differently
    // For now, we'll store the main value directly
    // Nested questions will be handled separately in the answers object
    currentArray[index] = itemValue;
    handleChange(currentArray);
  };
  
  const handleAddRepeatableItem = () => {
    const currentArray = Array.isArray(value) ? [...value] : [];
    const newIndex = currentArray.length; // Index of the new item
    
    // Add value based on question type and defaultValue
    let emptyValue = '';
    
    // For repeatable questions, use defaultValue if available (only for new items)
    if (question.defaultValue && question.defaultValue.trim() !== '') {
      // Use defaultValue for new items
      if (question.type === 'Checkbox') {
        // For Checkbox: defaultValue is comma-separated string, convert to array
        emptyValue = question.defaultValue.split(',').map(v => v.trim()).filter(Boolean);
      } else {
        // For RadioButton, DropDown, and other types: use defaultValue directly
        emptyValue = question.defaultValue.trim();
      }
    } else {
      // No defaultValue, use empty value based on question type
      if (question.type === 'Number') {
        emptyValue = 0;
      } else if (question.type === 'Date') {
        emptyValue = '';
      } else if (question.type === 'Checkbox') {
        emptyValue = []; // Checkbox should be array
      } else {
        emptyValue = '';
      }
    }
    
    // Generate a stable ID for the new item (used as React key)
    itemIdsRef.current.push(`${question.key}-id-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`);

    // Clear any stale composite keys for this new index, then add the item.
    // React 18 auto-batches all synchronous state updates — no need for requestAnimationFrame.
    if (onChange && answers) {
      const questionKeyPrefix = `${question.key}_${newIndex}_`;
      const keysToClear = Object.keys(answers).filter((key) => key.startsWith(questionKeyPrefix));
      keysToClear.forEach((key) => {
        onChange(key, null);
      });
    }
    handleChange([...currentArray, emptyValue]);
  };

  const handleRemoveRepeatableItem = (index) => {
    const currentArray = Array.isArray(value) ? [...value] : [];
    
    // Validate index
    if (index < 0 || index >= currentArray.length) {
      console.warn(`[QuestionRenderer] Invalid index for removal: ${index}, array length: ${currentArray.length}`);
      return;
    }
    
    // Remove the item at the specified index
    currentArray.splice(index, 1);

    // Remove the stable ID for this item
    itemIdsRef.current.splice(index, 1);

    if (onChange && answers) {
      // Collect composite keys to shift (itemIdx > index) or delete (itemIdx === index).
      // Format: `${question.key}_${itemIdx}_${nestedKey}`
      const shiftOperations = []; // {oldKey, newKey, value, itemIdx}
      const keysToDelete = new Set();

      const questionKeyPrefix = `${question.key}_`;
      const relevantKeys = Object.keys(answers).filter((key) => key.startsWith(questionKeyPrefix));

      relevantKeys.forEach((answerKey) => {
        const keySuffix = answerKey.substring(questionKeyPrefix.length);
        const firstUnderscoreIndex = keySuffix.indexOf('_');
        if (firstUnderscoreIndex === -1) return; // not a composite key

        const itemIdx = parseInt(keySuffix.substring(0, firstUnderscoreIndex), 10);
        if (isNaN(itemIdx)) return; // key prefix collision with another question key

        const nestedKey = keySuffix.substring(firstUnderscoreIndex + 1);

        if (itemIdx === index) {
          keysToDelete.add(answerKey);
        } else if (itemIdx > index) {
          const newKey = `${question.key}_${itemIdx - 1}_${nestedKey}`;
          shiftOperations.push({ oldKey: answerKey, newKey, value: answers[answerKey], itemIdx });
          keysToDelete.add(answerKey);
        }
      });

      // Shift highest index first to avoid overwriting conflicts (e.g. 3→2 before 2→1)
      shiftOperations.sort((a, b) => b.itemIdx - a.itemIdx);

      // React 18 auto-batches all synchronous state updates — no requestAnimationFrame needed.
      // Step 1: Update the array
      handleChange(currentArray);

      // Step 2: Write shifted values to new keys
      shiftOperations.forEach(({ newKey, value }) => {
        onChange(newKey, value);
      });

      // Step 3: Protect shift targets from deletion
      shiftOperations.forEach(({ newKey }) => {
        keysToDelete.delete(newKey);
      });

      // Step 4: Delete stale keys
      keysToDelete.forEach((key) => {
        onChange(key, null);
      });
    } else {
      handleChange(currentArray);
    }
  };

  // Render nested questions for selected answer branch
  const renderNestedQuestions = (answer, itemIndex = null) => {
    if (!answer.content?.nestedQuestions || !Array.isArray(answer.content.nestedQuestions)) {
      return null;
    }

    // CRITICAL: For repeatable questions, nested questions should only be rendered if:
    // 1. itemIndex is not null (a row has been added)
    // 2. The item value matches parentAnswerValue (if specified)
    if (question.repeatable && !question.parentId) {
      if (itemIndex === null) {
        // No row added yet, don't render nested questions
        return null;
      }
      // itemIndex is not null, so we can proceed
      // We'll filter nested questions by parentAnswerValue in the filter below
    }

    // For repeatable questions, nested questions need item-specific answers
    // Create a scoped answers object for this repeatable item
    let scopedAnswers = answers;
    let scopedOnChange = onNestedChange || onChange;
    
    if (question.repeatable && !question.parentId && itemIndex !== null && Array.isArray(value)) {
      // For repeatable questions, nested questions should use composite keys
      // Format: `${question.key}_${itemIndex}_${nestedKey}`
      // But we need to map back and forth between composite keys and regular keys
      scopedAnswers = {};
      // PERFORMANCE: Filter keys first, then process
      const itemKeyPrefix = `${question.key}_${itemIndex}_`;
      const itemKeys = Object.keys(answers).filter((key) => key.startsWith(itemKeyPrefix));
      
      // Extract answers for this specific item
      itemKeys.forEach((key) => {
        const nestedKey = key.substring(itemKeyPrefix.length);
        scopedAnswers[nestedKey] = answers[key];
      });
      
      // Also include non-composite keys (for non-repeatable nested questions)
      // Only add keys that don't start with any question key pattern
      Object.keys(answers).forEach((key) => {
        if (!key.includes('_') || !key.startsWith(`${question.key}_`)) {
          scopedAnswers[key] = answers[key];
        }
      });
      
      // Create scoped onChange that uses composite keys
      scopedOnChange = (nestedKey, nestedValue) => {
        const compositeKey = `${question.key}_${itemIndex}_${nestedKey}`;
        if (onChange) {
          onChange(compositeKey, nestedValue);
        }
      };
    }

    return (
      <Box
        sx={{
          mt: 2,
          ml: level * 2,
          pl: 2,
          borderLeft: '2px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary" gutterBottom>
          {answer.displayText} - Alt Sorular:
        </Typography>
        {answer.content.nestedQuestions
          .filter((nestedQ) => {
            // CRITICAL: Check visibility before rendering nested questions
            // For repeatable questions, nested questions should only be visible if:
            // 1. itemIndex is not null (a row has been added) - already checked above
            // 2. The item value matches parentAnswerValue
            if (question.repeatable && !question.parentId) {
              // itemIndex null check already done above, so we're safe here
              // Check if item value matches parentAnswerValue
              const itemValue = Array.isArray(value) ? value[itemIndex] : null;
              if (nestedQ.parentAnswerValue !== null && nestedQ.parentAnswerValue !== undefined) {
                if (itemValue !== nestedQ.parentAnswerValue) {
                  return false;
                }
              }
            } else {
              // For non-repeatable questions, check if current answer matches parentAnswerValue
              if (nestedQ.parentAnswerValue !== null && nestedQ.parentAnswerValue !== undefined) {
                const currentAnswer = answers[question.key];
                if (currentAnswer !== nestedQ.parentAnswerValue) {
                  return false;
                }
              }
            }
            
            // Use checkQuestionVisibility for additional checks (dependsOn, etc.)
            // For repeatable questions, use composite key for parentRepeatableKey
            const parentRepeatableKey = (question.repeatable && !question.parentId && itemIndex !== null)
              ? `${question.key}_${itemIndex}`
              : null;
            
            return checkQuestionVisibility(nestedQ, scopedAnswers, allQuestions, parentRepeatableKey);
          })
          .map((nestedQ) => {
            // Validate nested question object
            if (!nestedQ || !nestedQ.type) {
              console.error('[QuestionRenderer] Invalid nested question:', nestedQ);
              return (
                <Box key={nestedQ?._id || nestedQ?._tempId || nestedQ?.key || 'invalid'}>
                  <Typography variant="body2" color="error">
                    Geçersiz alt soru: {nestedQ ? JSON.stringify(nestedQ) : 'null'}
                  </Typography>
                </Box>
              );
            }

            // For nested questions, use defaultValue if value is undefined/null
            // This ensures defaultValue is applied for nested questions (including in repeatable)
            let nestedValue = scopedAnswers[nestedQ.key];
            if ((nestedValue === undefined || nestedValue === null) && 
                nestedQ.defaultValue && nestedQ.defaultValue.trim() !== '') {
              // Use defaultValue for display (AnswerForm's useEffect will handle the actual setting)
              if (nestedQ.type === "Checkbox") {
                nestedValue = nestedQ.defaultValue.split(',').map(v => v.trim()).filter(Boolean);
              } else {
                nestedValue = nestedQ.defaultValue;
              }
            }
            
            // Add parent question reference for LocationSelector dependsOn
            const nestedQuestionWithParent = {
              ...nestedQ,
              _parentQuestion: question,
            };
            
            return (
            <QuestionRenderer
              key={nestedQ._id || nestedQ._tempId || nestedQ.key}
              question={nestedQuestionWithParent}
                value={nestedValue}
                onChange={scopedOnChange}
                answers={scopedAnswers}
                onNestedChange={scopedOnChange}
                allQuestions={allQuestions}
              level={level + 1}
            />
            );
          })}
      </Box>
    );
  };

  // Render repeatable wrapper (for root questions with repeatable: true)
  const renderRepeatableWrapper = (renderSingleItem) => {
    if (!question.repeatable || question.parentId) {
      // Not repeatable or nested question - render normally with level padding
      return (
        <Box sx={{ mb: 2, pl: level * 2 }}>
          {renderSingleItem(value, 0)}
        </Box>
      );
    }

    // Repeatable question - render array of items
    const itemsArray = Array.isArray(value) ? value : [];

    // Ensure stable IDs exist for all current items (handles pre-populated data on mount)
    while (itemIdsRef.current.length < itemsArray.length) {
      itemIdsRef.current.push(`${question.key}-id-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`);
    }
    // Trim if the array somehow shrank without going through handleRemoveRepeatableItem
    if (itemIdsRef.current.length > itemsArray.length) {
      itemIdsRef.current.length = itemsArray.length;
    }

    return (
      <Box sx={{ mb: 2, pl: level * 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {question.displayText} (Çoklu Satır)
            </Typography>
            {question.description && question.description.trim() !== '' && (
              <Tooltip title={question.description} arrow placement="top">
                <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            )}
          </Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddRepeatableItem}
            variant="outlined"
          >
            Satır Ekle
          </Button>
        </Box>

        {itemsArray.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Henüz satır eklenmedi. "Satır Ekle" butonuna tıklayarak ekleyebilirsiniz.
          </Typography>
        )}

        {itemsArray.map((itemValue, index) => (
          <Box
            key={itemIdsRef.current[index]}
            sx={{
              mb: 2,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Satır {index + 1}
              </Typography>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemoveRepeatableItem(index)}
                sx={{ ml: 1 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            {renderSingleItem(itemValue, index)}
          </Box>
        ))}
      </Box>
    );
  };

  // Render single question input (used for both normal and repeatable)
  const renderSingleQuestion = (itemValue, itemIndex = 0) => {
  // Render based on question type
  switch (question.type) {
    case 'Text':
      return (
        <Box>
          {!question.repeatable && question.displayText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" component="label" sx={{ fontWeight: question.required ? 600 : 400 }}>
                {question.displayText}
                {question.required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {question.description && question.description.trim() !== '' && (
                <Tooltip title={question.description} arrow placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            type="text"
            label={question.repeatable ? undefined : undefined}
            value={itemValue !== undefined && itemValue !== null ? itemValue : ''}
            onChange={(e) => {
              if (question.repeatable && !question.parentId) {
                handleRepeatableChange(itemIndex, e.target.value);
              } else {
                handleChange(e.target.value);
              }
            }}
            required={question.required}
            placeholder={question.placeholder || question.defaultValue || ''}
            helperText={question.helperText || ''}
            error={question.required && !itemValue}
          />
          {/* Render nested questions if this is a RadioButton/DropDown answer branch */}
          {question.answers && Array.isArray(question.answers) && (
            <>
              {question.answers.map((answer) => {
                const isSelected = itemValue === answer.value;
                if (!isSelected) return null;
                return <Box key={answer.value}>{renderNestedQuestions(answer, itemIndex)}</Box>;
              })}
            </>
          )}
        </Box>
      );

    case 'TextArea':
      return (
        <Box>
          {!question.repeatable && question.displayText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" component="label" sx={{ fontWeight: question.required ? 600 : 400 }}>
                {question.displayText}
                {question.required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {question.description && question.description.trim() !== '' && (
                <Tooltip title={question.description} arrow placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label={question.repeatable ? undefined : undefined}
            value={itemValue !== undefined && itemValue !== null ? itemValue : ''}
            onChange={(e) => {
              if (question.repeatable && !question.parentId) {
                handleRepeatableChange(itemIndex, e.target.value);
              } else {
                handleChange(e.target.value);
              }
            }}
            required={question.required}
            placeholder={question.placeholder || question.defaultValue || ''}
            helperText={question.helperText || ''}
            error={question.required && !itemValue}
          />
          {/* Render nested questions if this is a RadioButton/DropDown answer branch */}
          {question.answers && Array.isArray(question.answers) && (
            <>
              {question.answers.map((answer) => {
                const isSelected = itemValue === answer.value;
                if (!isSelected) return null;
                return <Box key={answer.value}>{renderNestedQuestions(answer, itemIndex)}</Box>;
              })}
            </>
          )}
        </Box>
      );

    case 'Number':
      return (
        <Box>
          {!question.repeatable && question.displayText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" component="label" sx={{ fontWeight: question.required ? 600 : 400 }}>
                {question.displayText}
                {question.required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {question.description && question.description.trim() !== '' && (
                <Tooltip title={question.description} arrow placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            type="text"
            inputMode="numeric"
            inputProps={{ pattern: "[0-9.,]+" }}
            label={question.repeatable ? undefined : undefined}
            value={itemValue !== undefined && itemValue !== null ? itemValue : ''}
            onChange={(e) => {
              // Allow only numbers, dots, and commas
              const inputValue = e.target.value;
              const sanitizedValue = inputValue.replace(/[^0-9.,]/g, '');
              if (question.repeatable && !question.parentId) {
                handleRepeatableChange(itemIndex, sanitizedValue);
              } else {
                handleChange(sanitizedValue);
              }
            }}
            required={question.required}
            placeholder={question.placeholder || question.defaultValue || ''}
            helperText={question.helperText || 'Sadece sayı, nokta ve virgül girebilirsiniz (örn: 123.45 veya 123,45)'}
            error={question.required && !itemValue}
          />
          {/* Render nested questions if this is a RadioButton/DropDown answer branch */}
          {question.answers && Array.isArray(question.answers) && (
            <>
              {question.answers.map((answer) => {
                const isSelected = itemValue === answer.value;
                if (!isSelected) return null;
                return <Box key={answer.value}>{renderNestedQuestions(answer, itemIndex)}</Box>;
              })}
            </>
          )}
        </Box>
      );

    case 'Date':
      return (
        <Box>
          {!question.repeatable && question.displayText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" component="label" sx={{ fontWeight: question.required ? 600 : 400 }}>
                {question.displayText}
                {question.required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {question.description && question.description.trim() !== '' && (
                <Tooltip title={question.description} arrow placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            type="date"
            label={question.repeatable ? undefined : undefined}
            value={itemValue || ''}
            onChange={(e) => {
              if (question.repeatable && !question.parentId) {
                handleRepeatableChange(itemIndex, e.target.value);
              } else {
                handleChange(e.target.value);
              }
            }}
            required={question.required}
            InputLabelProps={{ shrink: true }}
            helperText={question.helperText || ''}
            error={question.required && !itemValue}
          />
          {/* Render nested questions if this is a RadioButton/DropDown answer branch */}
          {question.answers && Array.isArray(question.answers) && (
            <>
              {question.answers.map((answer) => {
                const isSelected = itemValue === answer.value;
                if (!isSelected) return null;
                return <Box key={answer.value}>{renderNestedQuestions(answer, itemIndex)}</Box>;
              })}
            </>
          )}
        </Box>
      );

    case 'Time':
      return (
        <Box>
          {!question.repeatable && question.displayText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" component="label" sx={{ fontWeight: question.required ? 600 : 400 }}>
                {question.displayText}
                {question.required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {question.description && question.description.trim() !== '' && (
                <Tooltip title={question.description} arrow placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            type="time"
            label={question.repeatable ? undefined : undefined}
            value={itemValue || ''}
            onChange={(e) => {
              if (question.repeatable && !question.parentId) {
                handleRepeatableChange(itemIndex, e.target.value);
              } else {
                handleChange(e.target.value);
              }
            }}
            required={question.required}
            InputLabelProps={{ shrink: true }}
            helperText={question.helperText || ''}
            error={question.required && !itemValue}
          />
        </Box>
      );

    case 'DateTime':
      return (
        <Box>
          {!question.repeatable && question.displayText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" component="label" sx={{ fontWeight: question.required ? 600 : 400 }}>
                {question.displayText}
                {question.required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {question.description && question.description.trim() !== '' && (
                <Tooltip title={question.description} arrow placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            type="datetime-local"
            label={question.repeatable ? undefined : undefined}
            value={itemValue || ''}
            onChange={(e) => {
              if (question.repeatable && !question.parentId) {
                handleRepeatableChange(itemIndex, e.target.value);
              } else {
                handleChange(e.target.value);
              }
            }}
            required={question.required}
            InputLabelProps={{ shrink: true }}
            helperText={question.helperText || ''}
            error={question.required && !itemValue}
          />
        </Box>
      );

    case 'Checkbox':
      return (
        <Box>
          <FormControl component="fieldset" required={question.required} error={question.required && (!itemValue || (Array.isArray(itemValue) && itemValue.length === 0))}>
            {!question.repeatable && question.displayText && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="body2" gutterBottom sx={{ mb: 0, fontWeight: question.required ? 600 : 400 }}>
                  {question.displayText}
                  {question.required && <span style={{ color: 'red' }}> *</span>}
                </Typography>
                {question.description && question.description.trim() !== '' && (
                  <Tooltip title={question.description} arrow placement="top">
                    <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                )}
              </Box>
            )}
            {question.options && Array.isArray(question.options) && question.options.length > 0 ? (
              <Box>
                {question.options.map((option) => {
                  const optionValue = option.value || option._id;
                  // CRITICAL: Only use defaultValue for display if itemValue is truly undefined/null
                  // If itemValue is empty array [], it means user cleared it, so don't use defaultValue
                  let displayValue = itemValue;
                  if ((displayValue === undefined || displayValue === null) && 
                      question.defaultValue && question.defaultValue.trim() !== '') {
                    // Use defaultValue ONLY if itemValue is truly undefined/null (first time, not cleared)
                    displayValue = question.defaultValue.split(',').map(v => v.trim()).filter(Boolean);
                  }
                  const isChecked = Array.isArray(displayValue) 
                    ? displayValue.includes(optionValue)
                    : displayValue === optionValue;
                  
                  return (
                    <FormControlLabel
                      key={optionValue}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => {
                            let newValue;
                            // CRITICAL: Use displayValue (which includes defaultValue if itemValue is undefined/null)
                            // This ensures that when user first interacts, we start from defaultValue, not empty array
                            // After first interaction, itemValue will be set, so we'll use that
                            const actualValue = itemValue !== undefined && itemValue !== null 
                              ? itemValue 
                              : (Array.isArray(displayValue) ? displayValue : []);
                            
                            if (Array.isArray(actualValue)) {
                              if (e.target.checked) {
                                // Add if not already present
                                if (!actualValue.includes(optionValue)) {
                                  newValue = [...actualValue, optionValue];
                                } else {
                                  newValue = actualValue; // Already present, no change
                                }
                              } else {
                                // Remove from array
                                newValue = actualValue.filter(v => v !== optionValue);
                              }
                            } else {
                              // Not an array, create new array
                              newValue = e.target.checked ? [optionValue] : [];
                            }
                            
                            if (question.repeatable && !question.parentId) {
                              handleRepeatableChange(itemIndex, newValue);
                            } else {
                              handleChange(newValue);
                            }
                          }}
                        />
                      }
                      label={option.displayText || option.value}
                    />
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="error">
                Seçenekler tanımlanmamış
              </Typography>
            )}
            {question.helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {question.helperText}
              </Typography>
            )}
          </FormControl>
        </Box>
      );

    case 'RadioButton':
      return (
        <Box>
          <FormControl fullWidth required={question.required} error={question.required && !itemValue} component="fieldset">
            {!question.repeatable && question.displayText && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="body2" gutterBottom sx={{ mb: 0, fontWeight: question.required ? 600 : 400 }}>
                  {question.displayText}
                  {question.required && <span style={{ color: 'red' }}> *</span>}
                </Typography>
                {question.description && question.description.trim() !== '' && (
                  <Tooltip title={question.description} arrow placement="top">
                    <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                )}
              </Box>
            )}
            <RadioGroup
              value={itemValue !== undefined && itemValue !== null ? itemValue : ''}
              onChange={(e) => {
                if (question.repeatable && !question.parentId) {
                  handleRepeatableChange(itemIndex, e.target.value);
                } else {
                  handleChange(e.target.value);
                }
              }}
              name={question.key}
            >
              {question.options && Array.isArray(question.options) ? (
                question.options.map((option) => (
                  <FormControlLabel
                    key={option.value || option._id}
                    value={option.value}
                    control={<Radio />}
                    label={option.displayText || option.value}
                  />
                ))
              ) : (
                <Typography variant="body2" color="error">
                  Seçenekler tanımlanmamış
                </Typography>
              )}
            </RadioGroup>
            {question.helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {question.helperText}
              </Typography>
            )}
          </FormControl>
          {/* Render nested questions for selected answer branch */}
          {question.answers && Array.isArray(question.answers) && (
            <>
              {question.answers.map((answer) => {
                // For repeatable: use itemValue, fallback to defaultValue if itemValue is empty
                const currentValue = itemValue !== undefined && itemValue !== null 
                  ? itemValue 
                  : (question.defaultValue || '');
                const isSelected = currentValue === answer.value;
                if (!isSelected) return null;
                return <Box key={answer.value}>{renderNestedQuestions(answer, itemIndex)}</Box>;
              })}
            </>
          )}
        </Box>
      );

    case 'DropDown':
      return (
        <Box>
          {!question.repeatable && question.displayText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" component="label" sx={{ fontWeight: question.required ? 600 : 400 }}>
                {question.displayText}
                {question.required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {question.description && question.description.trim() !== '' && (
                <Tooltip title={question.description} arrow placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          )}
          <FormControl fullWidth required={question.required} error={question.required && !itemValue}>
            <InputLabel>{question.repeatable ? undefined : undefined}</InputLabel>
            <Select
              value={itemValue !== undefined && itemValue !== null ? itemValue : ''}
              onChange={(e) => {
                if (question.repeatable && !question.parentId) {
                  handleRepeatableChange(itemIndex, e.target.value);
                } else {
                  handleChange(e.target.value);
                }
              }}
              label={question.repeatable ? undefined : undefined}
            >
              {question.options && Array.isArray(question.options) ? (
                question.options.map((option) => (
                  <MenuItem key={option.value || option._id} value={option.value}>
                    {option.displayText || option.value}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>Seçenekler tanımlanmamış</MenuItem>
              )}
            </Select>
            {question.helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {question.helperText}
              </Typography>
            )}
          </FormControl>
          {/* Render nested questions for selected answer branch */}
          {question.answers && Array.isArray(question.answers) && (
            <>
              {question.answers.map((answer) => {
                // For repeatable: use itemValue, fallback to defaultValue if itemValue is empty
                const currentValue = itemValue !== undefined && itemValue !== null 
                  ? itemValue 
                  : (question.defaultValue || '');
                const isSelected = currentValue === answer.value;
                if (!isSelected) return null;
                return <Box key={answer.value}>{renderNestedQuestions(answer, itemIndex)}</Box>;
              })}
            </>
          )}
        </Box>
      );

    case 'LocationSelector':
      return (
        <Box>
          {!question.repeatable && question.displayText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" component="label" sx={{ fontWeight: question.required ? 600 : 400 }}>
                {question.displayText}
                {question.required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {question.description && question.description.trim() !== '' && (
                <Tooltip title={question.description} arrow placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          )}
          <LocationSelector
            question={question}
            value={itemValue}
            onChange={(key, val) => {
              if (question.repeatable && !question.parentId) {
                handleRepeatableChange(itemIndex, val);
              } else {
                handleChange(val);
              }
            }}
            answers={answers}
            allQuestions={allQuestions}
            required={question.required}
          />
        </Box>
      );

    case 'Signature':
      return (
        <Box>
          {!question.repeatable && question.displayText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="body2" component="label" sx={{ fontWeight: question.required ? 600 : 400 }}>
                {question.displayText}
                {question.required && <span style={{ color: 'red' }}> *</span>}
              </Typography>
              {question.description && question.description.trim() !== '' && (
                <Tooltip title={question.description} arrow placement="top">
                  <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Box>
          )}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              bgcolor: 'background.paper',
              minHeight: 80,
              maxHeight: 100,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => {
              if (question.repeatable && !question.parentId) {
                // For repeatable questions, we need to handle the modal differently
                // For now, just open the modal
                setSignatureModalOpen(true);
              } else {
                setSignatureModalOpen(true);
              }
            }}
          >
            {itemValue && typeof itemValue === 'string' && itemValue.startsWith('data:image') ? (
              <Box
                component="img"
                src={itemValue}
                alt="İmza"
                sx={{
                  maxWidth: '200px',
                  maxHeight: 60,
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  İmza atmak için tıklayın
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {question.helperText || 'Çizim penceresi açılacaktır'}
                </Typography>
              </Box>
            )}
          </Box>
          {question.helperText && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {question.helperText}
            </Typography>
          )}
          <SignatureCanvasModal
            open={signatureModalOpen}
            value={itemValue}
            onClose={() => setSignatureModalOpen(false)}
            onSave={(base64Image) => {
              if (question.repeatable && !question.parentId) {
                handleRepeatableChange(itemIndex, base64Image);
              } else {
                handleChange(base64Image);
              }
              setSignatureModalOpen(false);
            }}
            question={question}
          />
        </Box>
      );

    default:
      return (
        <Box>
          <Typography variant="body2" color="error">
            Bilinmeyen soru tipi: {question.type}
          </Typography>
        </Box>
      );
  }
  };

  // Main render: Check if repeatable, then render accordingly
  return renderRepeatableWrapper(renderSingleQuestion);
};

QuestionRenderer.propTypes = {
  question: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  answers: PropTypes.object,
  onNestedChange: PropTypes.func,
  allQuestions: PropTypes.array,
  level: PropTypes.number,
};

QuestionRenderer.defaultProps = {
  value: null,
  answers: {},
  onNestedChange: null,
  allQuestions: [],
  level: 0,
};

export default QuestionRenderer;

